import React from 'react';
import { IndexRedirect, Route } from '@americanexpress/one-app-router';
import { updateLocale } from '@americanexpress/one-app-ducks';
import { Settings } from 'luxon';
import LandingPage from '@/components/landing/LandingPage';
import SearchPage from '@/components/search/SearchPage';
import LoungeDetailsPage from '@/components/details/LoungeDetailsPage';
import AllAirportsPage from '@/components/allAirports/AllAirportsPage';
import CulinaryCollectivePage from '@/components/CulinaryCollective/CulinaryCollectivePage';
import TermsAndConditionsPage from '@/components/TermsAndConditions/TermsAndConditionsPage';
import NotFound from '@/components/NotFound';
import {
  getSafeLocaleUtil,
  isInvalidLoungeFinderRoute,
  setAXPLocaleCookie,
} from '@/helpers/utils';
import CenturionNetworkPage from '@/components/CenturionNetwork';
import { canViewTCL, parseEnabledMarkets } from '@/helpers/featureFlagHelper';
import { getEnabledMarkets } from '@/selectors/configSelectors';
import { Card } from './constants';
import { locales } from './constants/supportedLocales';
import {
  centurionNetworkPageRoute,
  culinaryCollectivePageRoute,
} from './constants/routes';
import { TclMigrationFeatures } from './hooks/featureFlagHooks';

const setMatchedRouteStatus = (nextState, statusCode) => {
  const matchedLeafRoute = nextState.routes?.[nextState.routes.length - 1];
  if (matchedLeafRoute) {
    matchedLeafRoute.httpStatus = statusCode;
  }
};

const handleCountryRedirects = (props, replace) => {
  const { product, locale } = props.params;
  const { pathname } = props.location;
  const usLocale = locales.UnitedStates.toLocaleLowerCase();

  switch (locale) {
    case locales.Switzerland.toLocaleLowerCase():
      if (product === Card.CenturionIDCEN) {
        replace(pathname.replace(locale, usLocale));
      }
      break;
    default:
    // do not replace the URL
  }
};

const onEnterSanitizePathnameFromRoute =
  ({ dispatch, getState }) =>
  async (props, replace, onLoadComplete) => {
    // append trailing slash to all URLs
    // i.e. https://www.americanexpress.com/en-us/travel/lounges/the-platinum-card -> https://www.americanexpress.com/en-us/travel/lounges/the-platinum-card/
    const { pathname } = props.location;
    const currentURL = pathname;

    // redirect uppercase locale to lowercase,
    // e.g. /en-US/travel/lounges/the-platinum-card -> /en-us/travel/lounges/the-platinum-card
    const localeAllLower = props.params.locale?.toLowerCase();
    if (localeAllLower !== props.params.locale) {
      replace(currentURL.replace(props.params.locale, localeAllLower));
    }

    // handle country-specific redirects (edge cases such as Switzerland Centurion Card)
    handleCountryRedirects(props, replace);

    const matchedRoute = props.routes?.[props.routes.length - 1];
    const routePath = matchedRoute?.path;
    const state = getState();
    const enabledMarketProducts = parseEnabledMarkets(getEnabledMarkets(state));
    const hasInvalidProduct = isInvalidLoungeFinderRoute({
      locale: localeAllLower,
      product: props.params.product,
      enabledMarketProducts,
    });
    const isCenturionNetworkRoute =
      routePath === centurionNetworkPageRoute &&
      canViewTCL({
        state,
        feature: TclMigrationFeatures.CENTURION_NETWORK_PAGE,
      });
    const isCulinaryCollectiveRoute =
      routePath === culinaryCollectivePageRoute &&
      canViewTCL({
        state,
        feature: TclMigrationFeatures.CULINARY_COLLECTIVE_PAGE,
      });

    if (
      hasInvalidProduct &&
      !isCenturionNetworkRoute &&
      !isCulinaryCollectiveRoute
    ) {
      setMatchedRouteStatus(props, 404);
    }

    const unsafeLocale = props.params.locale;
    const safeLocale = getSafeLocaleUtil(unsafeLocale);
    if (safeLocale) {
      await dispatch(updateLocale(safeLocale));
    }

    Settings.defaultLocale = safeLocale?.slice(0, 2) || locales.UnitedStates;
    setAXPLocaleCookie(safeLocale); // Required for Analytics Tracking to reflect the correct locale.

    onLoadComplete();
  };

const childRoutes = (store) => {
  const state = store.getState();

  return [
    <Route
      key="localeHook"
      path="/"
      onEnter={onEnterSanitizePathnameFromRoute(store)}
    >
      {canViewTCL({
        state,
        feature: TclMigrationFeatures.CENTURION_NETWORK_PAGE,
      }) && (
        <Route
          path={centurionNetworkPageRoute}
          component={CenturionNetworkPage}
        />
      )}
      {canViewTCL({
        state,
        feature: TclMigrationFeatures.CULINARY_COLLECTIVE_PAGE,
      }) && (
        <Route
          path={culinaryCollectivePageRoute}
          component={CulinaryCollectivePage}
        />
      )}
      <Route path=":locale/travel/lounges/:product" component={LandingPage} />
      <Route
        path=":locale/travel/lounges/:product/terms"
        component={TermsAndConditionsPage}
      />
      <Route
        path=":locale/travel/lounges/:product/all-airports"
        component={AllAirportsPage}
      />
      <Route
        path=":locale/travel/lounges/:product/:airportCode"
        component={SearchPage}
      />
      <Route
        path=":locale/travel/lounges/:product/:airportCode/:loungeId"
        component={LoungeDetailsPage}
      />
      <Route path="*" component={NotFound} httpStatus={404} />
      <IndexRedirect to="en-us/travel/lounges/the-platinum-card" />
    </Route>,
  ];
};

export default childRoutes;
